import React, { useState, useEffect, useCallback } from "react"
import axios from "axios"
import RequestList from "./RequestList"

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000"

function AdminDashboard({ onLogout }) {
    const [requests, setRequests] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [filters, setFilters] = useState({ type: "all", status: "all", dateFrom: "", dateTo: "" })
    const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, swap: 0, off: 0 })
    const [exporting, setExporting] = useState(false)

    const updateStats = (data) => {
        const pending = data.filter(r => r.status === "pending").length
        const approved = data.filter(r => r.status === "approved").length
        const rejected = data.filter(r => r.status === "rejected").length
        const swap = data.filter(r => r.request_type === "Swap").length
        const off = data.filter(r => r.request_type === "Off").length
        setStats({ total: data.length, pending, approved, rejected, swap, off })
    }

    const fetchRequests = useCallback(async () => {
        try {
            const token = localStorage.getItem("adminToken")
            const params = new URLSearchParams()
            if (filters.type !== "all") params.append("type", filters.type)
            if (filters.status !== "all") params.append("status", filters.status)
            if (filters.dateFrom) params.append("date_from", filters.dateFrom)
            if (filters.dateTo) params.append("date_to", filters.dateTo)
            const url = `${API_URL}/api/requests${params.toString() ? "?" + params.toString() : ""}`
            const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } })
            setRequests(response.data)
            updateStats(response.data)
            setLoading(false)
        } catch (error) {
            console.error("Error fetching requests:", error)
            if (error.response?.status === 401) { 
                localStorage.removeItem("adminToken"); 
                localStorage.removeItem("adminUsername"); 
                onLogout() 
            } else { 
                setError("Failed to load requests"); 
                setLoading(false) 
            }
        }
    }, [filters, onLogout])

    useEffect(() => { 
        fetchRequests(); 
        const interval = setInterval(fetchRequests, 30000); 
        return () => clearInterval(interval) 
    }, [fetchRequests])

    const handleFilterChange = (e) => { 
        const { name, value } = e.target; 
        setFilters(prev => ({ ...prev, [name]: value })) 
    }
    
    const clearFilters = () => { 
        setFilters({ type: "all", status: "all", dateFrom: "", dateTo: "" }) 
    }

    const handleLogout = () => { 
        localStorage.removeItem("adminToken"); 
        localStorage.removeItem("adminUsername"); 
        onLogout() 
    }

    const handleBulkAction = () => {
        fetchRequests()
    }

    // ============================================
    // EXPORT CSV
    // ============================================

    const exportCSV = async () => {
        setExporting(true)
        try {
            const token = localStorage.getItem("adminToken")
            const response = await axios.get(`${API_URL}/api/export/csv`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            })
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `requests-${new Date().toISOString().split('T')[0]}.csv`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error("Error exporting CSV:", error)
            alert("Failed to export CSV. Please try again.")
        } finally {
            setExporting(false)
        }
    }

    if (loading) return <div className="loading"><div className="spinner"></div>Loading requests...</div>

    return (
        <div className="admin-dashboard">
            <div className="admin-header">
                <h2><i className="fas fa-chart-pie"></i> Dashboard</h2>
                <div className="admin-info">
                    <span><i className="fas fa-user-cog"></i> {localStorage.getItem("adminUsername")}</span>
                    <button onClick={exportCSV} className="export-btn" disabled={exporting}>
                        <i className="fas fa-file-export"></i> {exporting ? 'Exporting...' : 'Export CSV'}
                    </button>
                    <button onClick={handleLogout} className="logout-btn">
                        <i className="fas fa-sign-out-alt"></i> Logout
                    </button>
                </div>
            </div>
            
            <div className="stats-grid">
                <div className="stat-card total">
                    <span className="stat-icon"><i className="fas fa-chart-bar"></i></span>
                    <h3>Total</h3>
                    <p>{stats.total}</p>
                </div>
                <div className="stat-card pending">
                    <span className="stat-icon"><i className="fas fa-clock"></i></span>
                    <h3>Pending</h3>
                    <p>{stats.pending}</p>
                </div>
                <div className="stat-card approved">
                    <span className="stat-icon"><i className="fas fa-check-circle"></i></span>
                    <h3>Approved</h3>
                    <p>{stats.approved}</p>
                </div>
                <div className="stat-card rejected">
                    <span className="stat-icon"><i className="fas fa-times-circle"></i></span>
                    <h3>Rejected</h3>
                    <p>{stats.rejected}</p>
                </div>
                <div className="stat-card swap">
                    <span className="stat-icon"><i className="fas fa-exchange-alt"></i></span>
                    <h3>Swap</h3>
                    <p>{stats.swap}</p>
                </div>
                <div className="stat-card off">
                    <span className="stat-icon"><i className="fas fa-calendar-day"></i></span>
                    <h3>Off</h3>
                    <p>{stats.off}</p>
                </div>
            </div>
            
            <div className="filter-section">
                <h4><i className="fas fa-sliders-h"></i> Filter</h4>
                <div className="filter-grid">
                    <div className="filter-group">
                        <label>Type</label>
                        <select name="type" value={filters.type} onChange={handleFilterChange} className="filter-select">
                            <option value="all">All Types</option>
                            <option value="Swap"><i className="fas fa-exchange-alt"></i> Swap</option>
                            <option value="Off"><i className="fas fa-calendar-day"></i> Off</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Status</label>
                        <select name="status" value={filters.status} onChange={handleFilterChange} className="filter-select">
                            <option value="all">All Status</option>
                            <option value="pending"><i className="fas fa-clock"></i> Pending</option>
                            <option value="approved"><i className="fas fa-check-circle"></i> Approved</option>
                            <option value="rejected"><i className="fas fa-times-circle"></i> Rejected</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label><i className="fas fa-calendar-alt"></i> From</label>
                        <input type="date" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} className="filter-input" />
                    </div>
                    <div className="filter-group">
                        <label><i className="fas fa-calendar-alt"></i> To</label>
                        <input type="date" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} className="filter-input" />
                    </div>
                    <div className="filter-actions">
                        <button onClick={clearFilters} className="clear-filters-btn">
                            <i className="fas fa-undo"></i> Clear
                        </button>
                    </div>
                </div>
            </div>
            
            {error && <div className="error">{error}</div>}
            
            <RequestList 
                requests={requests} 
                onRequestUpdate={fetchRequests} 
                onRequestDelete={fetchRequests}
                onBulkAction={handleBulkAction}
            />
        </div>
    )
}

export default AdminDashboard